import Vue from 'vue';
import { Route } from 'vue-router';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { CancelTokenSource } from 'axios';
import * as Sentry from '@sentry/browser';

import { RESPONSE_TYPES } from '@/constants/response-types.constant';
import { PERSON_GROUPS } from '@/constants/person.constant';

import { PopularResponse } from '@/interfaces/responses/popular';
import { NewDayResponse, NewProviderResponse, NewResponse } from '@/interfaces/responses/new';
import Provider from '@/interfaces/provider';
import { TabType } from '@/interfaces/inner-tabs';
import { SingleProviderResponse } from '@/interfaces/responses/single-provider';
import { CollectionType } from '@/enums/collection-type';
import { TitleOrder } from '@/enums/title-order';
import { TitleListLayout } from '@/enums/title-list-layout';
import {
	Credit,
	NewTitle,
	OfferMonetizationTypes,
	PopularTitle,
	TitleObjectType,
	WatchlistTitleResponse,
} from '@/interfaces/titles';
import { ROLE_GROUP } from '@/enums/person';

import { FilterCollection } from '@/stores/filter.store';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import TitleHelper, { FetchTitlesOptions, HideSeenDislikedTitlesHelper } from '@/helpers/title-helper';
import { UtilsHelper } from '@/helpers/utils-helper';
import { getAuthHeaders, getAxios } from '@/helpers/axios-helper';
import FilterHelper from '@/helpers/filter-helper';
import { FreezingItems } from '@/store';
import { TitleListName } from '@/interfaces/responses/title-list';

export interface TitleStoreStateList extends PopularResponse {
	listLayout: TitleListLayout.CARD | TitleListLayout.GRID;
}

export interface InnerTab {
	watchlist: TabType;
}

/**
 * table of watchlist pagination tokes
 * used to manipulate list without losing the order
 */
let watchlistTitlesTokenTable: { [s: string]: any } = [];

const FALLBACK_PAGESIZE = 16;

// STATE
const state = () => ({
	fallback: {
		popular: [] as any,
		new: {} as NewResponse,
	},
	lists: {
		upcoming: {
			items: [] as any,
			total_results: 0,
			isFetching: false,
			listLayout: TitleListLayout.GRID,
			isAtEndOfList: false,
			page_size: 50,
		} as TitleStoreStateList,
		new: { days: [] as any, isFetching: false } as NewResponse,
		titlelist: {
			items: [] as any,
			total_results: 0,
			isFetching: false,
			listLayout: TitleListLayout.GRID,
			isAtEndOfList: false,
		} as TitleStoreStateList,
		// TODO We cannot remove watchlist for now, because we store selected listLayout here
		watchlist: {
			items: [] as any,
			total_results: 0,
			isFetching: false,
			listLayout: TitleListLayout.CARD,
			isAtEndOfList: false,
		} as TitleStoreStateList,
	} as Record<CollectionType, NewResponse | TitleStoreStateList>,

	newTimelineColdStart: true,

	personLists: {} as Record<ROLE_GROUP, PopularTitle[]>,

	/**
	 * what filters were used for the current list of each collectionType.
	 * this is used to determine whether titles have to be loaded.
	 */
	fetchedLists: {} as Record<CollectionType, { collectionState: string; query: string }>,

	/**
	 * only applies to loadTitles without append=true
	 */
	isFetching: false,

	/**
	 * a dictionary of which CollectionTypes is fetching.
	 */
	fetchingLists: Object.values(CollectionType).reduce((root, key) => {
		root[key] = false;
		return root;
	}, {} as Record<CollectionType, boolean>),

	timelineEndReached: {
		new: false,
	} as Record<CollectionType, boolean>,
});

export type TitleState = ReturnType<typeof state>;

export const TITLE_INIT_FREEZE_ITEMS: FreezingItems<TitleState> = {
	'fallback[*]': state => {
		(Object.keys(state.fallback) as (keyof TitleState['fallback'])[])
			.map(collectionType => state.fallback[collectionType])
			.filter(Boolean)
			.forEach((item: any) => Object.freeze(item));
	},
	'lists[*].items': state => {
		(Object.keys(state.lists) as CollectionType[])
			.map(collectionType => state.lists[collectionType].items)
			.filter(Boolean)
			.forEach((item: any) => Object.freeze(item));
	},
	'lists[*].days[].providers[].items': state => {
		(Object.keys(state.lists) as CollectionType[])
			.flatMap(collectionType => (state.lists[collectionType] as NewResponse).days)
			.filter(Boolean)
			.flatMap(day => day.providers)
			.map(provider => provider.items)
			.forEach(item => Object.freeze(item));
	},
};

// GETTERS
const getters: GetterTree<TitleState, any> = {
	/**
	 * whether the list returns the 'new' or 'popular' response.
	 */
	responseType: () => (collectionType: CollectionType) => {
		return RESPONSE_TYPES[collectionType];
	},
	popularFallbacks: state => {
		return state.fallback.popular;
	},
	newFallbacks: state => {
		return state.fallback.new;
	},
	titles:
		(state, getters, rootState) =>
		(collectionType: CollectionType): PopularResponse | NewResponse | TitleStoreStateList => {
			// @todo decide if using 'global' mode or 'single collection_type' mode
			if (rootState.person.filter !== ROLE_GROUP.ALL) {
				let list = state.personLists[rootState.person.filter as ROLE_GROUP];
				if (!list) {
					list = [];
				}
				return { items: list, total_results: list.length };
			}
			return state.lists[collectionType];
		},
	firstTitle: (state, getters) => (collectionType: CollectionType) => {
		return (getters.titles(collectionType) || [])[0];
	},
	listLayout: state => (collectionType: CollectionType) =>
		(state.lists[collectionType] as TitleStoreStateList)?.listLayout,
	providersTotal: state => (collectionType: CollectionType) => {
		switch (collectionType) {
			case CollectionType.NEW:
				const list = state.lists[collectionType] as NewResponse;
				return list.days ? list.days.reduce((count, day) => count + day.providers.length, 0) : 0;
		}
		return 0;
	},
	titlesPage: state => (collectionType: CollectionType) => {
		const list = state.lists[collectionType] || {};
		return list.page || 1;
	},
	titlesTotal:
		(state, _getters, rootState) => (collectionType: CollectionType, watchlistContentType?: 'show' | 'movie') => {
			const list = (state.lists[collectionType] || {}) as PopularResponse;
			let { total_results, items } = list;
			// to prevent CLS in x-axis on <TotalTitles> and when the watchlist hasn't loaded yet,
			// we can fallback to calculating the amount of titles from the stored titlelist values.
			if (total_results === 0 && collectionType === CollectionType.WATCHLIST && watchlistContentType) {
				total_results = Object.keys(rootState.titleList[TitleListName.WATCHLIST]).filter(
					(item: string) => item.indexOf(`${watchlistContentType}_`) === 0
				).length;
			}
			return total_results || (items && items.length) || 0;
		},
	/**
	 * whether titles need to be fetched anew.
	 * page size and page are ignored as only the 'filter' (FilterCollection) is relevant.
	 */
	isTitleListOutdated:
		state => (collectionType: CollectionType, collectionState: FilterCollection, routeQuery: Route['query']) => {
			if (collectionType === CollectionType.WATCHLIST) {
				return true;
			}
			return state.fetchedLists[collectionType]
				? state.fetchedLists[collectionType].collectionState !== JSON.stringify(collectionState) ||
						state.fetchedLists[collectionType].query !== JSON.stringify(routeQuery)
				: true;
		},
};

let cancelTokens: Set<CancelTokenSource> = new Set();

// ACTIONS
const actions: ActionTree<TitleState, any> = {
	getFiltersForCollectionType: async (
		{ state, rootState, rootGetters },
		{
			collectionType,
			filterByProviders,
		}: {
			collectionType: CollectionType;
			filterByProviders: boolean;
		}
	) => {
		const currentViewport = rootState.ui.viewport;
		let currentFilters = { ...rootGetters['filter/currentFilters'] };

		if (filterByProviders) {
			const { providers = [] } = rootState.user.settings;
			if (providers.length && currentFilters.providers.length === 0) {
				// if no provider is selected in the filter use all the user providers
				const userProviders = rootGetters['constant/providers']().map(
					(provider: Provider) => provider.short_name
				);
				currentFilters.providers = [...userProviders];
			}
		}

		// add more to filters
		if (collectionType === CollectionType.NEW) {
			currentFilters.titles_per_provider =
				currentViewport === 'smartphone'
					? TitleHelper.timelineProviderPageSize
					: global.NEW_TIMELINE_TITLES_PER_BUCKET_LIMIT;
		}

		// for the upcoming page we want to include shows
		if (collectionType === CollectionType.UPCOMING) {
			// this prevents "do not mutate vuex store state outside mutation handlers"
			currentFilters.content_types = [...currentFilters.content_types];

			if (currentFilters.content_types.length === 0) {
				currentFilters.content_types = [TitleObjectType.MOVIE, TitleObjectType.SHOW, TitleObjectType.SEASON];
			}

			if (currentFilters.content_types.includes(TitleObjectType.SHOW)) {
				currentFilters.content_types.push(TitleObjectType.SEASON);
			}

			currentFilters.is_upcoming = true;
		}

		// remove empty filters
		UtilsHelper.deleteEmptyPropsFromObject(currentFilters);

		if (collectionType === CollectionType.WATCHLIST) {
			// these filters are necessary to keep with fallbacks
			const { content_types, sort_asc = false, sort_by = TitleOrder.RELEVANCE, inner_tab } = currentFilters;

			// don't show buy/rent for ROW layout
			const titleList = state.lists[collectionType] as TitleStoreStateList;

			let watchlistFilters: any = {
				sort_asc,
				sort_by,
			};

			watchlistFilters = {
				...watchlistFilters,
				content_types,
				inner_tab,
			};

			currentFilters = { ...currentFilters, ...watchlistFilters };
		}

		return { ...currentFilters };
	},

	loadTitles: async (
		{ commit, dispatch, state, getters, rootState, rootGetters },
		{
			collectionType,
			append = false,
			routeQuery = {},
			paginationToken,
			matchingOffersOnly = false,
			filterByProviders = true,
			force = false,
			asFallback = false,
		}: {
			collectionType: CollectionType;
			append: boolean;
			routeQuery: Route['query'];
			paginationToken?: string;
			matchingOffersOnly?: boolean;
			filterByProviders?: boolean;
			force?: boolean;
			asFallback?: boolean;
		}
	): Promise<boolean> => {
		try {
			// if other requests are currently being fetch and this is not an append request then abort them all
			if (cancelTokens.size && !append) {
				cancelTokens.forEach(token => token.cancel());
				cancelTokens.clear();
			}

			const filters = await dispatch('getFiltersForCollectionType', { collectionType, filterByProviders });
			// checks if the base filters (FilterCollection) is not outdated so nothing needs to be fetched
			if (!force && !getters.isTitleListOutdated(collectionType, filters, routeQuery) && !append) {
				// nothing needs to be done since it has everything and usese the cached titles
				return false;
			}

			if (filters.max_runtime) {
				filters.max_runtime += 5;
			}

			if (filters.min_runtime) {
				filters.min_runtime -= 5;
			}

			commit('SET_FETCHING', { collectionType, value: true });

			const collectionResponse = state.lists[collectionType];
			let { page, page_size: pageSize } = TitleHelper.getPagingOptions(
				collectionType,
				append,
				collectionResponse,
				routeQuery,
				filters
			);

			if (collectionType === CollectionType.NEW) {
				if (process.client && append && state.newTimelineColdStart) {
					// trick for the client to correctly load new timeline after ssr
					// When first scroll happens we reload the whole first day
					// this is because we can't be more granular on our bucket list
					// see Ref #1 in title-helper.ts
					append = false;
					page = 1;
					commit('SET_NEW_TIMELINE_COLD_START', false);
				} else if (process.server) {
					commit('SET_NEW_TIMELINE_COLD_START', false);
				}
			}

			const locale = rootGetters['language/locale'];
			const language = rootGetters['language/language'];
			const providers = rootState.user.settings.providers;

			let options: FetchTitlesOptions = {
				locale,
				language,
				collectionType,
				filters,
				package_intersection: false,
				routeQuery,
				page,
				pageSize,
				matchingOffersOnly,
				providers,
			};

			// if a page has 0 results because of filters, show some top level (/xx/new or /xx) titles as alternatives
			if (asFallback && collectionType === CollectionType.POPULAR) {
				filters.providers = [];
				pageSize = FALLBACK_PAGESIZE;
			}

			if (asFallback && collectionType === CollectionType.NEW) {
				filters.providers = [];
			}

			if (collectionType === CollectionType.WATCHLIST) {
				const jwId = rootState.user.jwId;
				const accessToken = rootState.user.accessToken;

				options = {
					...options,
					paginationToken,
					jwId,
					accessToken,
				};

				const { inner_tab } = filters;
				let collectionTypeForRoute = collectionType as CollectionType;

				if (inner_tab === TabType.COMPLETED_SEEN) {
					collectionTypeForRoute = CollectionType.SEENLIST;
				}

				options.collectionType = collectionTypeForRoute;
			}

			const currentFilters = rootGetters['filter/currentFilters'];
			const activeFilters = FilterHelper.getActiveFilters(Object.keys(currentFilters) as any, currentFilters);
			const cacheable =
				// either no filters
				Object.keys(activeFilters).length === 0 ||
				// OR only 1 provider filter
				(Object.keys(activeFilters).length === 1 && (activeFilters.providers || []).length === 1);

			// fetch titles with cancelToken
			const [responsePromise, cancelTokenSource] = await TitleHelper.fetchTitlesWithAbort(
				options,
				currentFilters.providers?.length,
				cacheable
			);

			cancelTokens.add(cancelTokenSource);

			// decompose returned value
			let [response, isCanceled] = await responsePromise;

			// remove it after promise has been resolved
			cancelTokens.delete(cancelTokenSource);

			if (isCanceled) {
				return false;
			}

			// Set a flag to hide seen and disliked titles if user set that option
			if (rootState.user.hideSeenDislikedTitles) {
				/* New page */
				if (response.days) {
					// This whole logic for not showing the seen and dislike titles
					//is a work around until the Backend implements this on their side
					(response as NewResponse).days.map((day, indexDay) => {
						day.providers.map((provider, indexProvider) => {
							const currentProvider = response.days[indexDay].providers[indexProvider];
							const items = provider.items.map(HideSeenDislikedTitlesHelper.hideSeenDislikedTitle);

							currentProvider.items = items;
							currentProvider.total = currentProvider.total - items.filter(item => item.hideTitle).length;
						});
					});
					response.days = response.days.map(HideSeenDislikedTitlesHelper.showDayInNewPage);
				} else {
					// We are not recalculating the total on the Popular page because the total number of titles is
					//huge. Therefore, this will not lead to any weirdnesses
					response.items =
						response?.items?.map(HideSeenDislikedTitlesHelper.hideSeenDislikedTitle) ||
						response?.titles_full?.items?.map(HideSeenDislikedTitlesHelper.hideSeenDislikedTitle) ||
						[];
				}
			}

			if (append) {
				commit('APPEND_TITLES', { collectionType, response });
			} else {
				if (asFallback && collectionType === CollectionType.POPULAR) {
					commit('SET_FALLBACKS', { collectionType, titles: response.items ?? [] });
					commit('SET_FETCHING', { collectionType, value: false });
					return true;
				} else if (asFallback && collectionType === CollectionType.NEW) {
					commit('SET_NEW_FALLBACKS', { collectionType, days: response ?? [] });
					commit('SET_FETCHING', { collectionType, value: false });
					return true;
				} else {
					commit('SET_TITLES', { collectionType, response });
				}

				commit('SET_FETCHED', { collectionType, collectionState: filters, routeQuery });
			}

			commit('SET_FETCHING', { collectionType, value: false });

			// resetting timeline end reached
			commit('SET_TIMELINE_END_REACHED', { collectionType, value: false });

			// check if end of timeline has been reached
			if (collectionType === CollectionType.NEW) {
				if ((response as NewResponse).days.length === 0) {
					commit('SET_TIMELINE_END_REACHED', { collectionType, value: true });
				}
			}

			// check if it's a person
			const person_id = routeQuery.person_id as string;
			if (person_id) {
				const personId = parseInt(person_id);
				await dispatch('person/loadPerson', personId, { root: true });
				const items: PopularTitle[] = (response as PopularResponse).items;
				const lists = {} as { [key in ROLE_GROUP]: PopularTitle[] };
				const listCounts = {} as { [key in ROLE_GROUP]: number };
				lists[ROLE_GROUP.ALL] = [];
				if (items && items.length) {
					dispatch('person/loadBackdropForTitle', items[0], { root: true });
					items.forEach((title: PopularTitle) => {
						if (!title.credits) {
							return;
						}

						const roleGroups = {} as { [key in ROLE_GROUP]: boolean };
						title.credits.forEach((credit: Credit) => {
							roleGroups[PERSON_GROUPS[credit.role]] = true;
						});

						for (const roleGroup in ROLE_GROUP) {
							if (!roleGroups[roleGroup as ROLE_GROUP]) {
								continue;
							}

							if (!lists[roleGroup as ROLE_GROUP]) {
								lists[roleGroup as ROLE_GROUP] = [];
							}
							lists[roleGroup as ROLE_GROUP].push(title);
						}
						lists[ROLE_GROUP.ALL].push(title);
					});
					Object.keys(lists).map(key => {
						listCounts[key as ROLE_GROUP] = lists[key as ROLE_GROUP].length;
					});
				}
				commit('person/SET_LIST_COUNTS', listCounts, { root: true });
				commit('SET_PERSON_LISTS', lists);
			} else {
				// dispatch('person/reset', undefined, { root: true });
			}
		} catch (err) {
			const error: any = err;
			commit('SET_FETCHING', { collectionType, value: false });
			captureMessageForSentry(
				'Load title issue:',
				{ error, where: '[title.store.ts] loadTitles' },
				Sentry.Severity.Error
			);
		}
		return true;
	},

	loadMoreTitles: async (
		{ dispatch, getters, rootState },
		payload: {
			event: any;
			collectionType: CollectionType;
			paginationToken?: string;
			matchingOffersOnly?: boolean;
			filterByProviders?: boolean;
		}
	) => {
		const {
			event,
			collectionType,
			paginationToken,
			matchingOffersOnly = false,
			filterByProviders = true,
		} = payload;

		let listLen = 0;

		const routeQuery =
			collectionType === CollectionType.SEARCH && rootState.routing.activeRoute.query
				? rootState.routing.activeRoute.query
				: {};

		return dispatch('loadTitles', {
			collectionType,
			append: true,
			routeQuery,
			paginationToken,
			matchingOffersOnly,
			filterByProviders,
		})
			.then(() => {
				// tell infinite scroll to be completed
				const { days = [], items = [] } = getters.titles(collectionType);
				listLen = items.length || days.length;
			})
			.finally(() => {
				event.target && event.target.complete(listLen);
			});
	},

	loadMoreTitlesHorizontally: async (
		{ rootGetters, commit, state, rootState },
		payload: {
			complete: Function;
			collectionType: CollectionType;
			date: string;
			provider: Provider;
			dayProvider: NewProviderResponse;
		}
	) => {
		// new
		const { complete, collectionType, date, provider, dayProvider } = payload;
		try {
			const paging = TitleHelper.getTimelinePagingOptions(dayProvider);

			const locale = rootGetters['language/locale'];
			const currentFilters = rootGetters['filter/currentFilters'];
			const filters = {
				...currentFilters,
				provider: provider.short_name,
				date,
			};

			let response = await TitleHelper.fetchProviderTitles(
				locale,
				collectionType,
				filters,
				paging.page,
				paging.page_size
			);

			// Set a flag to hide seen and disliked titles if user set that option
			if (rootState.user.hideSeenDislikedTitles) {
				response.items =
					(response?.items?.map(HideSeenDislikedTitlesHelper.hideSeenDislikedTitle) as NewTitle[]) || [];
			}

			commit('APPEND_TITLES_HORIZONTALLY', { response, dayProvider });

			// make horizontal scrollable ready again for new infinite scroll requests
			// this function needs to be called to say the horizontal scroll component that it's ready to request more
			complete();
		} catch (error) {
			const err: any = error;
			// Mark the request as completed even if it fails
			complete();

			captureMessageForSentry(
				'Timeline issue:',
				{ error: err, where: '[title.store.ts] loadMoreTitlesHorizontally' },
				Sentry.Severity.Error
			);
		}
	},

	loadTitleList: async ({ commit, getters, rootGetters }, titleListId: number) => {
		const collectionType = 'titlelist';
		const filters = { ...rootGetters['filter/currentFilters'] };
		const locale = rootGetters['language/locale'];

		if (!getters.isTitleListOutdated(collectionType, filters)) {
			// nothing needs to be done since it has everything and usese the cached titles
			return;
		}

		commit('SET_FETCHING', { collectionType, value: true });

		const params = {
			body: JSON.stringify({ ...filters, ...{ page: 1, page_size: 1000 } }),
		};
		const raw = await getAxios().get(`${JW_CONFIG.API}/title_list/${titleListId}/full/locale/${locale}`, {
			params,
		});
		const response = raw.data.titles_full;

		commit('SET_TITLES', { collectionType, response });
		commit('SET_FETCHED', { collectionType, collectionState: filters, query: {} });
		commit('SET_FETCHING', { collectionType, value: false });
	},

	loadOnboardingList: async (
		{ rootGetters, rootState },
		likedJwIds: string[] = [],
		numberOfTitles = 24,
		fields: string[] = ['poster', 'id', 'object_type']
	) => {
		const locale = rootGetters['language/locale'];
		const jwId = rootState.user.jwId;
		const accessToken = rootState.user.accessToken;

		const body = {
			count: numberOfTitles,
			fields,
			new_liked_jw_entity_ids: likedJwIds,
		};
		const apiUrl = `${JW_CONFIG.DISCOVERY_URL}/taste_survey/${locale}/next_titles`;
		// Best Efford ID Strategy
		let params = {};
		if (jwId) {
			params = {
				justwatch_id: jwId,
			};
		}
		const headers = await getAuthHeaders(accessToken);

		let response;

		try {
			response = await getAxios().post(apiUrl, body, { params, headers });
			const { data } = response;

			if (!data.next_titles.length) {
				// if next_titles is empty we need to reset taste_survey
				throw new Error('[Gefjon] needs reset');
			}
		} catch (error) {
			// when Exception occurs reset taste_survey
			try {
				const apiUrl = `${JW_CONFIG.DISCOVERY_URL}/taste_survey/${locale}/reset?justwatch_id=${jwId}`;
				response = await getAxios().post(apiUrl, undefined, { headers });
			} catch (err) {
				const error: any = err;
				captureMessageForSentry('Onboarding Status Issue: Gefjon issue', {
					where: '[title.store.ts] loadOnboardingList Gefjon reset unsuccesful',
					error,
				});

				return null;
			}
		}
		return response && response.data;
	},

	removeFromList: async ({ commit }, { paginationTokens }: { paginationTokens: string[] }) => {
		commit('REMOVE_WATCHLIST_TITLES', paginationTokens);
	},

	refreshWatchList: async (
		{ commit, dispatch, state, rootState, rootGetters },
		{ paginationTokens }: { titleListName: string; paginationTokens: string[] }
	) => {
		const append = false;
		const routeQuery = {};
		const collectionType = CollectionType.WATCHLIST;

		let filters = await dispatch('getFiltersForCollectionType', { collectionType });

		commit('SET_FETCHING', { collectionType, value: true });

		const collectionResponse = state.lists[collectionType];
		const paging = TitleHelper.getPagingOptions(collectionType, append, collectionResponse, routeQuery);

		if (paginationTokens.length) {
			filters = { ...filters, pagination_tokens: paginationTokens };
		}

		if (!filters.providers) {
			filters.providers = rootState.user.settings.providers;
		}

		const locale = rootGetters['language/locale'];
		let options: FetchTitlesOptions = {
			locale,
			collectionType,
			filters,
			package_intersection: false,
			routeQuery,
			page: paging.page,
			pageSize: paging.page_size,
		};

		const jwId = rootState.user.jwId;
		const accessToken = rootState.user.accessToken;
		const language = rootGetters['language/language'];
		const { inner_tab } = filters;
		let collectionTypeForRoute = collectionType as CollectionType;
		if (inner_tab === TabType.COMPLETED_SEEN) {
			collectionTypeForRoute = CollectionType.SEENLIST;
		}

		options = {
			...options,
			jwId,
			accessToken,
			language,
			collectionType: collectionTypeForRoute,
		};

		if (paginationTokens.length === 1) {
			options.paginationToken = paginationTokens[0];
		}

		const [promise] = await TitleHelper.fetchTitlesWithAbort(options);
		const [response] = await promise;

		// @ts-ignore
		const items = response?.items || response?.titles_full?.items;
		if (items?.length) {
			commit('REPLACE_WATCHLIST_TITLES', items);
		} else {
			commit('REMOVE_WATCHLIST_TITLES', filters.pagination_tokens);
		}

		commit('SET_FETCHED', { collectionType, collectionState: filters, query: {} });
		commit('SET_FETCHING', { collectionType, value: false });
	},

	resetTitles: async (
		{ commit },
		{
			collectionType,
		}: {
			collectionType: CollectionType;
		}
	) => {
		const emptyResponse = { items: [], total_results: 0, page: 1, page_size: 5 };
		commit('SET_TITLES', {
			collectionType,
			response: emptyResponse,
		});
	},
	setListLayout: (
		{ commit },
		{ collectionType, listLayout }: { collectionType: CollectionType; listLayout: TitleListLayout }
	) => {
		commit('SET_LIST_LAYOUT', { collectionType, listLayout });
	},
};

// MUTATIONS
const mutations: MutationTree<TitleState> = {
	APPEND_TITLES: (
		state,
		{
			collectionType,
			response,
		}: {
			collectionType: CollectionType;
			// TODO: ts enthusiasts fix this
			response: any;
		}
	) => {
		switch (collectionType) {
			case CollectionType.NEW:
			case CollectionType.PRICEDROPS: {
				let days = (state.lists[collectionType] as NewResponse).days;

				// temporary workaround for 321-duplicate-days-in-new-timeline-ssr
				const duplicatedDay = days.find(day => day.date === response.days[0]?.date);

				if (duplicatedDay) {
					break;
				}

				// add new response to the list
				days = days
					.concat(response.days as NewDayResponse[])
					// make sure to freeze the items inside the provider items
					.map(item => {
						item.providers?.flatMap(provider => provider.items).forEach(item => Object.freeze(item));
						return item;
					});
				response = response as NewResponse;
				state.lists[collectionType] = {
					days,
					page: response.page,
					page_size: response.page_size,
					skip_count: response.skip_count,
				};
				break;
			}
			case CollectionType.WATCHLIST: {
				if (response) {
					// response.title_full is the object of the old watchlist
					response = response.titles_full || response;
					if (response.items) {
						// update token table to track correct order
						watchlistTitlesTokenTable.push(...createTokenTable(response.items));
						const items = (state.lists[collectionType] as TitleStoreStateList).items.concat(response.items);
						state.lists[collectionType] = {
							total_results: response.total_results,
							items: items.map(item => Object.freeze(item)),
							page: response.page,
							page_size: response.page_size,
							listLayout: (state.lists[collectionType] as TitleStoreStateList).listLayout,
							isAtEndOfList: !response.items.length,
						};
					}
				}
				break;
			}
			default: {
				response = response as PopularResponse;
				const items = (state.lists[collectionType] as TitleStoreStateList).items.concat(response.items);
				state.lists[collectionType] = {
					total_results: response.total_results,
					items: items.map(item => Object.freeze(item)),
					page: response.page,
					page_size: response.page_size,
					listLayout: (state.lists[collectionType] as TitleStoreStateList).listLayout,
					isAtEndOfList:
						!response.items.length || (!!response.page_size && response.items.length < response.page_size),
				};
			}
		}
	},
	APPEND_TITLES_HORIZONTALLY: (
		state,
		payload: { response: SingleProviderResponse; dayProvider?: NewProviderResponse }
	) => {
		const { response, dayProvider } = payload;
		if (dayProvider == null) return;

		Vue.set(
			dayProvider,
			'items',
			dayProvider.items.concat(response.items).map((item: any) => Object.freeze(item))
		);
	},
	SET_TITLES: (state, { collectionType, response }) => {
		switch (collectionType) {
			case CollectionType.WATCHLIST:
				if (response) {
					// response.title_full is the object of the old watchlist
					response = response.titles_full || response;
					if (response.items) {
						watchlistTitlesTokenTable = createTokenTable(response.items);
						Vue.set(state.lists, collectionType, {
							items: response.items.map((item: any) => Object.freeze(item)),
							total_results: response.total_results,
							page: response.page,
							page_size: response.page_size,
							listLayout: (state.lists[collectionType as CollectionType] as TitleStoreStateList)
								.listLayout,
							isAtEndOfList: !response.items.length,
						});
					}
				}
				break;
			case CollectionType.NEW: {
				// add provider_id to all items in each provider
				// therefore this mess 😅
				const value = {
					...response,
					days: response.days.map((day: NewDayResponse) => {
						return {
							...day,
							providers: day.providers.map(provider => {
								return {
									...provider,
									items: provider.items.map(item => {
										return Object.freeze({
											...item,
											offers: item.offers.map(offer => {
												return {
													...offer,
													provider_id: provider.provider_id,
												};
											}),
										});
									}),
								};
							}),
						};
					}),
				};
				Vue.set(state.lists, collectionType, value);
				break;
			}
			default:
				Vue.set(state.lists, collectionType, {
					listLayout: (state.lists[collectionType as CollectionType] as TitleStoreStateList).listLayout,
					...response,
					items: response.items.map((item: any) => Object.freeze(item)),
					isAtEndOfList:
						!response.items.length || (!!response.page_size && response.items.length < response.page_size),
				});
				break;
		}
	},
	SET_FALLBACKS: (state, { collectionType, titles }) => {
		if (collectionType === CollectionType.POPULAR) {
			Vue.set(
				state.fallback,
				collectionType,
				titles.map((title: any) => Object.freeze(title))
			);
		}
	},
	SET_NEW_FALLBACKS: (state, { collectionType, days }) => {
		if (collectionType === CollectionType.NEW) {
			Vue.set(state.fallback, collectionType, days);
		}
	},
	SET_PERSON_LISTS: (state, lists: Record<ROLE_GROUP, PopularTitle[]>) => {
		state.personLists = lists;
	},
	SET_FETCHED: (
		state,
		payload: { collectionType: CollectionType; collectionState: FilterCollection; routeQuery: Route['query'] }
	) => {
		const { collectionType, collectionState, routeQuery } = payload;
		Vue.set(state.fetchedLists, collectionType, {
			collectionState: JSON.stringify(collectionState),
			query: JSON.stringify(routeQuery),
		});
	},
	SET_FETCHING: (state, { collectionType, value }: { collectionType: CollectionType; value: boolean }) => {
		state.fetchingLists[collectionType] = value;
	},
	SET_TIMELINE_END_REACHED: (
		state,
		{ collectionType, value }: { collectionType: CollectionType; value: boolean }
	) => {
		if (collectionType in state.timelineEndReached) {
			state.timelineEndReached[collectionType] = value;
		}
	},
	SET_LIST_LAYOUT: (state, { collectionType, listLayout }) => {
		(state.lists[collectionType as CollectionType] as TitleStoreStateList).listLayout = listLayout;
	},

	/**
	 * Remove title from the watchlist (added to the seenlist)
	 * based on pagination token
	 *
	 * uses: watchlistTitlesTokenTable
	 */
	REMOVE_WATCHLIST_TITLES: (state, tokens) => {
		let updatedWatchlistTitles: any[] = [];
		let { items: watchlistTitles } = state.lists[CollectionType.WATCHLIST];

		const removeTitleWithToken = (token: any) => (title: any) => {
			return (title.pagination_token || title.id) !== token;
		};

		if (tokens && tokens.length) {
			tokens.forEach((token: any) => {
				updatedWatchlistTitles = watchlistTitles.filter(removeTitleWithToken(token));
			});

			const currentList = state.lists[CollectionType.WATCHLIST];
			currentList.items = updatedWatchlistTitles;
			// Updated total_results count
			if ('total_results' in currentList && typeof currentList!.total_results !== 'undefined') {
				let diff = watchlistTitles.length - updatedWatchlistTitles.length;
				currentList!.total_results = currentList!.total_results - diff;
			}
			Vue.set(state.lists, CollectionType.WATCHLIST, currentList);
		}
	},

	/**
	 * Replace or add title from the watchlist (removed from the seenlist)
	 * based on pagination token
	 *
	 * uses: watchlistTitlesTokenTable
	 */
	REPLACE_WATCHLIST_TITLES: (state, titles) => {
		let { items: watchlistTitles } = state.lists[CollectionType.WATCHLIST];
		const titleWithToken = (token: string) => (t: any) => t.pagination_token === token;
		const getTitleIndexFromToken = (pagination_token: string) => {
			const currentTable = createTokenTable(watchlistTitles);
			currentTable.push(pagination_token);
			// to get the position of where the object was I intersect the watchlistTitlesTokenTable (which holds
			// the order information) with the current one (plus the object to restore)
			const intersection = watchlistTitlesTokenTable.filter((token: string) => currentTable.includes(token));
			return intersection.indexOf(pagination_token) || 0;
		};

		if (titles && titles.length) {
			if (titles.length > 1) {
				titles.forEach((title: any) => {
					const { pagination_token } = title;

					const index = getTitleIndexFromToken(pagination_token);
					let replace = 0;
					if (watchlistTitles.find(titleWithToken(pagination_token))) {
						replace = 1;
					}

					watchlistTitles.splice(index, replace, title);
				});
			} else {
				watchlistTitles = watchlistTitles.map((titleObject: WatchlistTitleResponse) => {
					if (titleObject.title.id === titles[0].title.id) {
						// If subheading changed, keep old subheading until page restart
						if (titleObject.list_subheading?.technical_name !== titles[0].list_subheading?.technical_name) {
							titles[0].list_subheading.technical_name = titleObject.list_subheading?.technical_name;
						}
						return titles[0];
					}

					return titleObject;
				});
			}

			const currentList = state.lists[CollectionType.WATCHLIST];
			currentList.items = watchlistTitles;
			Vue.set(state.lists, CollectionType.WATCHLIST, currentList);
		}
	},

	SET_NEW_TIMELINE_COLD_START: (state, value) => {
		state.newTimelineColdStart = value;
	},
};

const createTokenTable = function (items: any[] = []) {
	return items.map((item: any) => item.pagination_token || item.id);
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
} as Module<TitleState, any>;
